/**
 * Party Person API Contract
 * 
 * This contract defines the API interface for person operations.
 * It lives in the domain package to maintain proper dependency direction.
 * 
 * Platform → Domains (never reverse)
 */

import { z } from 'zod';

// ============================================================================
// Core Schemas
// ============================================================================

/**
 * Person role type enumeration
 */
export const PersonRoleTypeSchema = z.enum(['Human', 'AiAgent']);

/**
 * Core person schema - unified data structure
 */
export const PersonSchema = z.object({
  // Identity
  partyId: z.string().uuid(),
  
  // Names
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  displayName: z.string().optional(), // Computed from first/last
  
  // Contact
  email: z.string().email(),
  phone: z.string().optional(),
  
  // Classification
  roleType: PersonRoleTypeSchema,
  
  // Professional
  jobTitle: z.string().optional(),
  department: z.string().optional(),
  organizationId: z.string().uuid().optional(),
  organizationName: z.string().optional(),
  managerId: z.string().uuid().optional(),
  managerName: z.string().optional(),
  
  // Status
  isActive: z.boolean().default(true),
  
  // Metrics
  directReportsCount: z.number().int().min(0).default(0),
  
  // Security
  mfaEnabled: z.boolean().default(false),
  accountLocked: z.boolean().default(false),
  
  // Timestamps
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

/**
 * Person list item - optimized for list views
 */
export const PersonListItemSchema = PersonSchema.pick({
  partyId: true,
  firstName: true,
  lastName: true,
  email: true,
  roleType: true,
  jobTitle: true,
  department: true,
  isActive: true,
  organizationName: true,
  createdAt: true,
  updatedAt: true,
});

/**
 * Person detail view - includes computed fields
 */
export const PersonDetailSchema = PersonSchema.extend({
  // Full organization context
  organizationContext: z.object({
    organizationId: z.string().uuid(),
    organizationName: z.string(),
    organizationType: z.string(),
    department: z.string().optional(),
  }).optional(),
  
  // Management hierarchy
  managementInfo: z.object({
    managerId: z.string().uuid().optional(),
    managerName: z.string().optional(),
    directReports: z.array(z.object({
      partyId: z.string().uuid(),
      displayName: z.string(),
      jobTitle: z.string().optional(),
    })),
  }).optional(),
  
  // Security profile
  securityProfile: z.object({
    mfaEnabled: z.boolean(),
    accountLocked: z.boolean(),
    failedLoginAttempts: z.number().int().min(0),
    lastLoginAt: z.string().datetime().optional(),
    totalLoginCount: z.number().int().min(0),
  }).optional(),
});

// ============================================================================
// Request/Response Schemas
// ============================================================================

/**
 * Search criteria for filtering persons
 */
export const PersonSearchCriteriaSchema = z.object({
  // Pagination
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(10),
  
  // Filters
  searchTerm: z.string().optional(),
  email: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  roleType: PersonRoleTypeSchema.optional(),
  isActive: z.boolean().optional(),
  department: z.string().optional(),
  jobTitle: z.string().optional(),
  organizationId: z.string().uuid().optional(),
  managerId: z.string().uuid().optional(),
  
  // Sorting
  sortBy: z.enum(['firstName', 'lastName', 'email', 'createdAt', 'updatedAt', 'department']).default('firstName'),
  sortOrder: z.enum(['asc', 'desc']).default('asc'),
});

/**
 * Person list response
 */
export const PersonListResponseSchema = z.object({
  success: z.boolean(),
  data: z.array(PersonListItemSchema),
  metadata: z.object({
    total: z.number().int().min(0),
    page: z.number().int().min(1),
    limit: z.number().int().min(1),
    hasMore: z.boolean(),
  }),
  timestamp: z.string().datetime(),
});

/**
 * Single person response
 */
export const PersonDetailResponseSchema = z.object({
  success: z.boolean(),
  data: PersonDetailSchema,
  timestamp: z.string().datetime(),
});

/**
 * Create person request
 */
export const CreatePersonRequestSchema = z.object({
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  email: z.string().email(),
  roleType: PersonRoleTypeSchema,
  phone: z.string().optional(),
  jobTitle: z.string().optional(),
  department: z.string().optional(),
  organizationId: z.string().uuid().optional(),
  managerId: z.string().uuid().optional(),
});

/**
 * Update person request
 */
export const UpdatePersonRequestSchema = CreatePersonRequestSchema.partial();

/**
 * Error response schema
 */
export const PersonErrorResponseSchema = z.object({
  success: z.literal(false),
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.any().optional(),
  }),
  timestamp: z.string().datetime(),
});

// ============================================================================
// Contract Definition
// ============================================================================

/**
 * Complete person API contract
 */
export const PersonContract = {
  /**
   * List persons with filtering and pagination
   */
  list: {
    method: 'GET' as const,
    path: '/api/party/persons',
    queryParams: PersonSearchCriteriaSchema,
    response: PersonListResponseSchema,
    errorResponse: PersonErrorResponseSchema,
  },
  
  /**
   * Get single person by ID
   */
  get: {
    method: 'GET' as const,
    path: '/api/party/persons/:partyId',
    params: z.object({ partyId: z.string().uuid() }),
    response: PersonDetailResponseSchema,
    errorResponse: PersonErrorResponseSchema,
  },
  
  /**
   * Create new person
   */
  create: {
    method: 'POST' as const,
    path: '/api/party/persons',
    body: CreatePersonRequestSchema,
    response: PersonDetailResponseSchema,
    errorResponse: PersonErrorResponseSchema,
  },
  
  /**
   * Update existing person
   */
  update: {
    method: 'PUT' as const,
    path: '/api/party/persons/:partyId',
    params: z.object({ partyId: z.string().uuid() }),
    body: UpdatePersonRequestSchema,
    response: PersonDetailResponseSchema,
    errorResponse: PersonErrorResponseSchema,
  },
  
  /**
   * Delete person (soft delete)
   */
  delete: {
    method: 'DELETE' as const,
    path: '/api/party/persons/:partyId',
    params: z.object({ partyId: z.string().uuid() }),
    response: z.object({
      success: z.boolean(),
      message: z.string(),
      timestamp: z.string().datetime(),
    }),
    errorResponse: PersonErrorResponseSchema,
  },
};

// ============================================================================
// Type Exports
// ============================================================================

export type Person = z.infer<typeof PersonSchema>;
export type PersonListItem = z.infer<typeof PersonListItemSchema>;
export type PersonDetail = z.infer<typeof PersonDetailSchema>;
export type PersonSearchCriteria = z.infer<typeof PersonSearchCriteriaSchema>;
export type PersonListResponse = z.infer<typeof PersonListResponseSchema>;
export type PersonDetailResponse = z.infer<typeof PersonDetailResponseSchema>;
export type CreatePersonRequest = z.infer<typeof CreatePersonRequestSchema>;
export type UpdatePersonRequest = z.infer<typeof UpdatePersonRequestSchema>;
export type PersonErrorResponse = z.infer<typeof PersonErrorResponseSchema>;

// ============================================================================
// Validation Helpers
// ============================================================================

/**
 * Validate person list response
 */
export function validatePersonListResponse(data: unknown): data is PersonListResponse {
  return PersonListResponseSchema.safeParse(data).success;
}

/**
 * Validate person detail response
 */
export function validatePersonDetailResponse(data: unknown): data is PersonDetailResponse {
  return PersonDetailResponseSchema.safeParse(data).success;
}

/**
 * Validate and parse person list response
 */
export function parsePersonListResponse(data: unknown): PersonListResponse {
  return PersonListResponseSchema.parse(data);
}

/**
 * Validate and parse person detail response
 */
export function parsePersonDetailResponse(data: unknown): PersonDetailResponse {
  return PersonDetailResponseSchema.parse(data);
}